import React from 'react';
import { getTotalTableCount, getTotalSensitiveTableCount, getOverviewData } from 'api';
import { filterJobStartable } from 'DataSources/datasourcesApi';
import { commafy, EngineIcon, transformOverviewData, truncateCenter } from 'Utils';
import { components } from 'react-select';
import { FaUnlock } from 'react-icons/fa';
import { BsSearch } from 'react-icons/bs';
import DiscoveryTooltip from 'DiscoveryTooltip';

const dataInit = [
  {
    color: '#315DFF',
    innerRadius: 100,
    radius: 140,
    percent: 25,
    theta: 95
  },
  {
    color: '#7360F6',
    innerRadius: 100,
    radius: 140,
    percent: 50,
    theta: 85
  },
  {
    color: '#00C4F0',
    innerRadius: 100,
    radius: 140,
    percent: 25,
    theta: 90
  },
  {
    color: '#315DFF',
    innerRadius: 100,
    radius: 140,
    percent: 25,
    theta: 100
  },
  {
    color: '#7360F6',
    innerRadius: 100,
    radius: 140,
    percent: 50,
    theta: 75
  },
  {
    color: '#00C4F0',
    innerRadius: 100,
    radius: 140,
    percent: 25,
    theta: 100
  }
];

const getSearchStatusData = (searchStatuses) => {
  const result = [];
  const map = {};

  // Get map from child to parent search ID values.
  searchStatuses.forEach((status, i) => map[status.searchid] = status);
  try {
    searchStatuses.forEach(status => {
      if (status && status.parent_searchid) {
        const parent = status.parent_searchid;
        if (map[parent]) {
          status.type = map[parent].type;
        }
      }
      const processedRows = [];
      for (const key in status) {
        if (key.includes('_processed')) processedRows.push(status[key]);
      }
      const totalRows = status.total_rows;
      const totalProcessed = status.total_processed;
      const item = {
        ...status,
        ...totalRows && ({ total_rows: totalRows }),
        ...totalProcessed && ({ total_processed: totalProcessed })
      };
      result.push(item);
    })
  } catch(e) {
    console.log(e);
  }

  return result;
}

const getConnectionOptions = (connections = []) => {
  const result = [];

  try {
    // Imported-but-not-adopted data sources have no stored credentials, so any
    // discover run against them fails. Hide them from the dropdown entirely.
    const actionable = filterJobStartable(connections);
    actionable.forEach((connection) => {
      const icon = <EngineIcon engine={connection.type} size='1.1rem' classes='mr-2' />
      const item =  {
        value: connection,
        label: connection.name,
        icon: icon,
      };
      result.push(item);
    })
  } catch(e) {
    console.log('getConnectionOptions error', e);
  }
  
  return result;
}

const getRulesListOptions = (rulesList = []) => {
  const result = [];
  try {
    rulesList.forEach((rules) => {
      const item = {
        value: rules,
        label: rules.name
      };
      result.push(item);
    })
  } catch(e) {
    console.log(e);
  }

  return result;
}

const getPreviousSearchData = async (searchid) => {
  try {
    const [
      overviewData,
      totalTableCountValue, 
      totalSensitiveTableCountValue
    ] = await Promise.all([
        getOverviewData(searchid),
        getTotalTableCount(searchid),
        getTotalSensitiveTableCount(searchid)
    ]).catch((e) => {
      throw e;
    });
    const overviewDataValue = transformOverviewData(overviewData) || {};
    return { overviewDataValue, totalTableCountValue, totalSensitiveTableCountValue };
  } catch(e) {
    console.log(e);
    throw e;
  }
};

const getDataVis = (rules) => {
  let result = [];

  const rowSpec = rules && rules.sample && rules.sample.row;
  if (!rowSpec || rowSpec.size == null || rowSpec.every == null) {
    return result;
  }

  try {
    const rowSize = rowSpec.size;
    const everySize = rowSpec.every;

    let percent = Math.floor(rowSize / rowSpec.every * 100);
    let angleSampled = percent / 100 * 360;
    let angleRows = 360 - angleSampled;

    let arr = [
      {
        angle: angleSampled,
        color: "#315DFF",
        label: 'Sampled Data',
        subLabel: `${percent}% of rows`,
        legendText: `Sampling ${rowSize} rows`,
        rowCount: rowSize
      },
      {
        angle: angleRows,
        color: "#D9DDE3",
        label: 'Database Rows',
        legendText: `Out of every ${commafy(everySize)} rows`,
        rowCount: everySize
      }
    ];
    result = arr;
  } catch(e) {
    console.log(e);
  }

  return result;
}

const getSelectedOption = (data = {}, options = [], key = 'str') => {
  let selectedOption = null;

  try {
    options.forEach((obj) => {
      const value = obj && obj.value || {};
      if (value[key] === data[key]) {
        selectedOption = obj;
        return selectedOption;
      }
    })
  } catch(e) {
    console.log(e);
  }

  return selectedOption;
}

const getDefaultOption = (items, localKey, itemKey) => {
  let result = items[0] || {};
  
  try {
    const lastItemName = localStorage.getItem(localKey);
    const lastObj = items.find((obj) => obj[itemKey] === lastItemName);
    if (lastObj) result = lastObj;
  } catch(e) {
    console.log(e);
  }

  return result;
}

const Option = ({ children, ...props }) => {
  const data = props.data;
  const value = data && data.value;
  const helperText = value && value.type === 's3' ? (
    `S3 Bucket, ${truncateCenter(value.host, 30, '..')}`
  ) : (
    `${value && value.type} database`
  );

  const optionContent = (
    <div className='d-flex flex-row'>
      {/* { value && value.checkedOut &&
        <div id='checked-out-connection-option'>
          <FaUnlock size='1.25rem' color='#b7c3ce' />
        </div>
      } */}
      <div className='mr-2'>
        {data && data.icon}
      </div>
      <div>
        <div>
          {children}
        </div>
        { value && (
          <div className='select-box-helper'>
            {helperText}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <components.Option className='position-relative' {...props}>
      { props.isDisabled ? (
        <DiscoveryTooltip placement='top'
          text='No more available Data Sources.'
        >
          {optionContent}
        </DiscoveryTooltip>
      ) : (
        optionContent
      )}
    </components.Option>
  )
};

const SingleValue = ({ children, ...props }) => {
  const data = props.data;
  const value = data && data.value;
  const helperText = value && value.type === 's3' ? (
    `S3 Bucket, ${truncateCenter(value.host, 30, '..')}`
  ) : (
    `${value && value.type} database`
  );

  return (
    <components.SingleValue className='position-relative d-flex flex-row' {...props}>
      {/* { value && value.checkedOut &&
        <div id='checked-out-connection-single-value'>
          <FaUnlock size='1.25rem' color='#b7c3ce' />
        </div>
      } */}
      <div className='mr-2'>
        {data && data.icon}
      </div>

      <div>
        <div>
          {children}
        </div>
        { value && (
          <div className='select-box-helper'>
            { helperText }
          </div>
        )}
      </div>
    </components.SingleValue>
  )
};

const Input = ({ children, ...props }) => {
  return (
    <div className='select-box-input-container'>
      { props.value.length > 0 && <BsSearch className='select-box-search-icon'/>}
      <components.Input className='select-box-input' {...props} />
      { props.value.length > 0 && (
        <div className='select-box-search-helper-text select-box-helper'>
          Filter by Data Source name or type.
        </div>
      )}
    </div>
  )
}

const customFilter = (option, searchText) => {
  const search = searchText.toLowerCase();

  // data values
  const data = option.data || {};
  const label = data.label && data.label.toLowerCase();

  // option values
  const value = option.value || {};
  const type = value.type && value.type.toLowerCase();
  const database = value.database && value.database.toLowerCase();
  const arr = [label, type, database];
  return arr.some(val => val && val.includes(search));
}

export {
  customFilter,
  dataInit,
  getConnectionOptions,
  getDefaultOption,
  getDataVis,
  getSearchStatusData,
  getSelectedOption,
  getRulesListOptions,
  getPreviousSearchData,
  Input,
  Option,
  SingleValue
};